/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector(`[data-id="${id}"]`);
      // Initialise from URL so load-more continues from the right page after refresh
      const urlParams = new URLSearchParams(window.location.search);
      this.page = parseInt(urlParams.get('page')) || 1;
    }

    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.initLoadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.initInfiniteScroll();
      }
      // On refresh, silently prepend all pages before the current one
      if (this.page > 1) {
        this.restorePreviousPages();
      }
    }

    // URL pushed to browser history (no section_id)
    buildPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      return url.pathname + url.search;
    }

    // URL used for fetching — adds section_id so Shopify returns only the section fragment
    buildFetchUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      url.searchParams.set('section_id', this.dataset.section);
      return url.pathname + url.search;
    }

    initLoadMore() {
      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    initInfiniteScroll() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      this.observer.observe(this);
    }

    async loadProducts() {
      if (this.hasAttribute('loading')) return;

      this.page++;
      this.setAttribute('loading', 'true');
      const historyUrl = this.buildPageUrl(this.page);
      const fetchUrl = this.buildFetchUrl(this.page);

      try {
        const response = await fetch(fetchUrl);
        const responseText = await response.text();
        const hasMore = this.renderProducts(responseText);

        // Keep the URL in sync so a refresh lands on the right page
        window.history.replaceState({}, '', historyUrl);
        this.updateFilterCount(responseText);

        if (!hasMore) {
          if (this.observer) this.observer.unobserve(this);
          if (this.button) this.button.classList.add('visually-hidden');
        }

        dispatchCustomEvent('pagination:page-changed', { url: historyUrl });
      } catch (error) {
        console.error('Error loading products:', error);
        this.page--; // revert so the next click retries the same page
      } finally {
        this.removeAttribute('loading');
      }
    }

    // Fetch pages 1 … N-1 in parallel and prepend them above the already-rendered page N
    async restorePreviousPages() {
      const currentPage = this.page;
      const promises = [];

      for (let p = 1; p < currentPage; p++) {
        promises.push(fetch(this.buildFetchUrl(p)).then(r => r.text()));
      }

      try {
        const texts = await Promise.all(promises);
        const fragment = document.createDocumentFragment();

        texts.forEach(html => {
          const gridEl = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
          if (!gridEl) return;
          gridEl.querySelectorAll('li.columns').forEach(item => {
            item.querySelectorAll('.lazyload').forEach(img => lazySizes.loader.unveil(img));
            fragment.appendChild(item);
          });
        });

        // Preserve scroll position while inserting content above
        const scrollBefore = window.pageYOffset;
        const heightBefore = document.documentElement.scrollHeight;

        const firstItem = this.grid.querySelector('li.columns');
        if (firstItem) {
          this.grid.insertBefore(fragment, firstItem);
        } else {
          this.grid.appendChild(fragment);
        }

        requestAnimationFrame(() => {
          const heightAfter = document.documentElement.scrollHeight;
          window.scrollTo(0, scrollBefore + (heightAfter - heightBefore));
        });

        if (typeof initProductCardSizeSelectors === 'function') initProductCardSizeSelectors(this.grid);
        if (typeof initSizeOpenButtons === 'function') initSizeOpenButtons(this.grid);

      } catch (error) {
        console.error('Error restoring previous pages:', error);
      }
    }

    // Sync the product count / progress bar with the page that was just fetched
    updateFilterCount(html) {
      const countEl = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
      if (!countEl) return;
      document.querySelectorAll('.thb-filter-count').forEach(el => {
        el.innerHTML = countEl.innerHTML;
      });
    }

    // Append new product items from the fetched HTML; returns false when there are no more products
    renderProducts(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const gridToReplace = doc.getElementById('product-grid');
      if (!gridToReplace) return false;

      const products = gridToReplace.querySelectorAll('li.columns');
      if (!products.length) return false;

      products.forEach((product) => {
        this.grid.appendChild(product);
        product.querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

      if (typeof initProductCardSizeSelectors === 'function') initProductCardSizeSelectors(this.grid);
      if (typeof initSizeOpenButtons === 'function') initSizeOpenButtons(this.grid);

      // Last page: the fetched section has no next page
      const loadMoreBtn = doc.querySelector('.load-more');
      if (!loadMoreBtn || loadMoreBtn.dataset.lastPage === 'true') return false;

      return true;
    }
  }

  customElements.define('pagination-theme', Pagination);
}
